/**
 * Governança — Gestão de Categorias do Staff
 * Atribuir/remover categorias a TREINADOR, PREPARADOR_FISICO
 *
 * Endpoints consumed:
 *   GET  /api/rbac/staff-scope?club_id=&sport=  (aggregator)
 *   POST /api/rbac/staff-categories/assign       { staff_user_id, club_id, sport, category_name }
 *   POST /api/rbac/staff-categories/remove        { staff_user_id, club_id, sport, category_name }
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, RefreshCw, AlertTriangle, Info, Plus, X, Shield,
  UserCheck, ChevronDown, Search
} from 'lucide-react';
import { AZURE } from '../../services/api';
import { COLORS, STYLES } from '../../components/governance/governanceTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const STAFF_LABELS = {
  pt: {
    title: 'Categorias do Staff',
    subtitle: 'Atribuir categorias a treinadores e preparadores — define quais atletas cada staff pode acessar',
    refresh: 'Atualizar',
    sportLabel: 'Esporte',
    clubLabel: 'Clube',
    sportFootball: 'Futebol',
    sportSwimming: 'Natação',
    loadingClubs: 'Carregando clubes...',
    selectClub: 'Selecione um clube',
    emptyTitle: 'Selecione um Clube',
    emptyDesc: 'Escolha o esporte e clube para gerenciar as categorias do staff (treinador, preparador físico, auxiliar).',
    loadingData: 'Carregando dados do clube...',
    categoriesIn: (name) => `Categorias em ${name}`,
    noCategories: 'Nenhuma categoria encontrada. Crie squads com categoria neste clube primeiro.',
    athletes: 'atletas',
    staffHeader: (n) => `Staff (${n})`,
    searchPlaceholder: 'Buscar staff...',
    thStaff: 'Staff',
    thRole: 'Role',
    thCategories: 'Categorias Atribuídas',
    thActions: 'Ações',
    noStaff: 'Nenhum staff com role de coaching neste clube.',
    noResults: 'Nenhum resultado para a busca.',
    noCategory: 'Sem categoria — vê atletas via vínculo direto',
    assign: 'Atribuir',
    assignTitle: 'Atribuir Categoria',
    allAssigned: (name) => `${name} já está em todas as categorias disponíveis.`,
    assignSelectFor: 'Selecione uma categoria para',
    close: 'Fechar',
    cancel: 'Cancelar',
    removeTitle: 'Remover Categoria',
    removeConfirm: (cat, staff) => `Remover ${cat} de ${staff}?`,
    removeWarning: 'O staff perderá acesso aos atletas dessa categoria.',
    remove: 'Remover',
  },
  en: {
    title: 'Staff Categories',
    subtitle: 'Assign categories to coaches and trainers — defines which athletes each staff member can access',
    refresh: 'Refresh',
    sportLabel: 'Sport',
    clubLabel: 'Club',
    sportFootball: 'Football',
    sportSwimming: 'Swimming',
    loadingClubs: 'Loading clubs...',
    selectClub: 'Select a club',
    emptyTitle: 'Select a Club',
    emptyDesc: 'Choose the sport and club to manage staff categories (coach, fitness trainer, assistant).',
    loadingData: 'Loading club data...',
    categoriesIn: (name) => `Categories in ${name}`,
    noCategories: 'No categories found. Create squads with a category in this club first.',
    athletes: 'athletes',
    staffHeader: (n) => `Staff (${n})`,
    searchPlaceholder: 'Search staff...',
    thStaff: 'Staff',
    thRole: 'Role',
    thCategories: 'Assigned Categories',
    thActions: 'Actions',
    noStaff: 'No staff with coaching role in this club.',
    noResults: 'No results for this search.',
    noCategory: 'No category — sees athletes via direct link',
    assign: 'Assign',
    assignTitle: 'Assign Category',
    allAssigned: (name) => `${name} is already in all available categories.`,
    assignSelectFor: 'Select a category for',
    close: 'Close',
    cancel: 'Cancel',
    removeTitle: 'Remove Category',
    removeConfirm: (cat, staff) => `Remove ${cat} from ${staff}?`,
    removeWarning: 'The staff member will lose access to athletes in this category.',
    remove: 'Remove',
  },
};

// === ROLE DISPLAY MAP ===
const ROLE_META = {
  TREINADOR: { label: 'Treinador', color: '#111111', bg: '#f3f4f6' },
  PREPARADOR_FISICO: { label: 'Prep. Físico', color: '#92400e', bg: '#fef3c7' },
};

const RoleBadge = ({ roleKey }) => {
  const m = ROLE_META[roleKey] || { label: roleKey, color: '#374151', bg: '#e5e7eb' };
  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap"
      style={{ backgroundColor: m.bg, color: m.color }}
    >
      {m.label}
    </span>
  );
};

// === CATEGORY PILL ===
const CategoryPill = ({ name, count, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-800 border border-gray-200">
    {name}
    {count !== undefined && (
      <span className="text-gray-500 font-normal">({count})</span>
    )}
    {onRemove && (
      <button
        onClick={onRemove}
        className="ml-0.5 hover:text-red-600 transition-colors"
        title="Remover categoria"
      >
        <X size={12} />
      </button>
    )}
  </span>
);

// === ASSIGN MODAL ===
const AssignModal = ({ staff, categories, assignedCategories, onAssign, onClose, t }) => {
  const available = categories.filter(c => !assignedCategories.includes(c.category_name));

  if (available.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-bold text-gray-900 mb-3">{t.assignTitle}</h3>
          <p className="text-gray-600 mb-4">
            <strong>{staff.full_name}</strong> {t.allAssigned(staff.full_name).split(staff.full_name).pop()}
          </p>
          <button onClick={onClose} className={STYLES.buttonSecondary + ' w-full'}>
            {t.close}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{t.assignTitle}</h3>
        <p className="text-sm text-gray-500 mb-4">
          {t.assignSelectFor} <strong>{staff.full_name}</strong> <RoleBadge roleKey={staff.role_key} />
        </p>

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {available.map(cat => (
            <button
              key={cat.category_name}
              onClick={() => onAssign(cat.category_name)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-400 transition-all text-left"
            >
              <span className="font-medium text-gray-900">{cat.category_name}</span>
              <span className="text-xs text-gray-500">{cat.athletes_count} {t.athletes}</span>
            </button>
          ))}
        </div>

        <button onClick={onClose} className={STYLES.buttonSecondary + ' w-full mt-4'}>
          {t.cancel}
        </button>
      </div>
    </div>
  );
};

// === CONFIRM REMOVE MODAL ===
const ConfirmRemoveModal = ({ staff, categoryName, onConfirm, onClose, t }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-bold text-gray-900 mb-3">{t.removeTitle}</h3>
      <p className="text-gray-600 mb-4">
        {t.removeConfirm(categoryName, staff.full_name)}
      </p>
      <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3 mb-4">
        <AlertTriangle size={14} className="inline mr-1" />
        {t.removeWarning}
      </p>
      <div className="flex gap-3">
        <button onClick={onClose} className={STYLES.buttonSecondary + ' flex-1'}>
          {t.cancel}
        </button>
        <button
          onClick={onConfirm}
          className={STYLES.buttonDanger + ' flex-1'}
        >
          {t.remove}
        </button>
      </div>
    </div>
  </div>
);

// === MAIN PAGE ===
const GovernanceStaffScopesPage = () => {
  const [clubs, setClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [selectedSport, setSelectedSport] = useState('football');
  const [scopeData, setScopeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignModal, setAssignModal] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const { lang } = useGovernanceLang();
  const t = STAFF_LABELS[lang] || STAFF_LABELS.pt;

  // --- fetch clubs (on mount) ---
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        // Use sports/clubs endpoint which returns all clubs for governance
        const { data } = await AZURE.get('/sports/clubs', { params: { sport: selectedSport, limit: 500 } });
        if (data.success) setClubs(data.data || []);
      } catch (err) {
        console.error('[StaffScopes] clubs fetch error:', err);
      } finally {
        setLoadingClubs(false);
      }
    };
    fetchClubs();
  }, [selectedSport]);

  // --- fetch scope data when club+sport changes ---
  const fetchScopeData = useCallback(async () => {
    if (!selectedClubId || !selectedSport) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await AZURE.get('/rbac/staff-scope', {
        params: { club_id: selectedClubId, sport: selectedSport }
      });
      if (data.success) {
        setScopeData(data.data);
      } else {
        setError(data.error || 'Erro ao carregar dados');
      }
    } catch (err) {
      console.error('[StaffScopes] fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedClubId, selectedSport]);

  useEffect(() => { fetchScopeData(); }, [fetchScopeData]);

  // --- assign handler ---
  const handleAssign = async (categoryName) => {
    if (!assignModal) return;
    setActionLoading(true);
    try {
      await AZURE.post('/rbac/staff-categories/assign', {
        staff_user_id: assignModal.staff.user_id,
        club_id: selectedClubId,
        sport: selectedSport,
        category_name: categoryName
      });
      setAssignModal(null);
      fetchScopeData();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // --- remove handler ---
  const handleRemove = async () => {
    if (!removeModal) return;
    setActionLoading(true);
    try {
      await AZURE.post('/rbac/staff-categories/remove', {
        staff_user_id: removeModal.staff.user_id,
        club_id: selectedClubId,
        sport: selectedSport,
        category_name: removeModal.categoryName
      });
      setRemoveModal(null);
      fetchScopeData();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // --- filtered staff ---
  const filteredStaff = (scopeData?.staff || []).filter(s =>
    s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.role_key?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedClub = clubs.find(c => c.club_id === selectedClubId);

  return (
    <div>
      {/* ─── Header ─── */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
          <div className="flex-1">
            <h2 className="text-2xl font-extrabold text-gray-900">
              {t.title}
            </h2>
            <p className="text-sm text-gray-500">
              {t.subtitle}
            </p>
          </div>
          {scopeData && (
            <button
              onClick={fetchScopeData}
              disabled={loading}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center gap-2 text-sm"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              {t.refresh}
            </button>
          )}
        </div>

        {/* ─── Club + Sport Selector ─── */}
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">{t.sportLabel}</label>
            <select
              value={selectedSport}
              onChange={e => { setSelectedSport(e.target.value); setSelectedClubId(null); setScopeData(null); }}
              className={STYLES.select}
            >
              <option value="football">{t.sportFootball}</option>
              <option value="swimming">{t.sportSwimming}</option>
            </select>
          </div>
          <div className="flex-[2] min-w-[280px]">
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">{t.clubLabel}</label>
            {loadingClubs ? (
              <div className="px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-400 text-sm">
                {t.loadingClubs}
              </div>
            ) : (
              <select
                value={selectedClubId || ''}
                onChange={e => setSelectedClubId(Number(e.target.value) || null)}
                className={STYLES.select}
              >
                <option value="">{t.selectClub}</option>
                {clubs.map(c => (
                  <option key={c.club_id} value={c.club_id}>{c.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* ─── Error ─── */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-2">
          <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* ─── Empty State (no club selected) ─── */}
      {!selectedClubId && !loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">{t.emptyTitle}</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">{t.emptyDesc}</p>
        </div>
      )}

      {/* ─── Loading ─── */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <RefreshCw size={32} className="animate-spin text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">{t.loadingData}</p>
        </div>
      )}

      {/* ─── Scope Data ─── */}
      {scopeData && !loading && (
        <>
          {/* ─── Categories Summary ─── */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-600 uppercase mb-3 flex items-center gap-2">
              <Info size={14} /> {t.categoriesIn(selectedClub?.name || `Club ${selectedClubId}`)}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {scopeData.categories.length === 0 ? (
                <div className="col-span-full bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
                  <AlertTriangle size={14} className="inline mr-1" />
                  {t.noCategories}
                </div>
              ) : (
                scopeData.categories.map(cat => (
                  <div
                    key={cat.category_name}
                    className="bg-white rounded-lg border border-gray-200 p-3 text-center hover:shadow-sm transition-shadow"
                  >
                    <div className="text-2xl font-extrabold" style={{ color: COLORS.primary }}>
                      {cat.athletes_count}
                    </div>
                    <div className="text-xs font-bold text-gray-600 mt-1">{cat.category_name}</div>
                    <div className="text-[10px] text-gray-400">{t.athletes}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ─── Staff Table ─── */}
          <div className={STYLES.panel}>
            <div className={STYLES.panelHeader + ' flex items-center justify-between'}>
              <div className="flex items-center gap-2">
                <UserCheck size={16} className="text-gray-500" />
                <span className="text-sm font-bold text-gray-700">
                  {t.staffHeader(filteredStaff.length)}
                </span>
              </div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500"
                />
              </div>
            </div>

            {filteredStaff.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                {(scopeData.staff || []).length === 0
                  ? t.noStaff
                  : t.noResults
                }
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className={STYLES.table}>
                  <thead className={STYLES.tableHeader}>
                    <tr>
                      <th className={STYLES.tableHeaderCell}>{t.thStaff}</th>
                      <th className={STYLES.tableHeaderCell}>{t.thRole}</th>
                      <th className={STYLES.tableHeaderCell}>{t.thCategories}</th>
                      <th className={STYLES.tableHeaderCell + ' text-center'}>{t.thActions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredStaff.map(staff => (
                      <tr key={`${staff.user_id}-${staff.role_key}`} className={STYLES.tableRow}>
                        {/* Name + Avatar */}
                        <td className={STYLES.tableCell}>
                          <div className="flex items-center gap-3">
                            {staff.avatar_url ? (
                              <img
                                src={staff.avatar_url}
                                alt=""
                                className="w-8 h-8 rounded-full object-cover border border-gray-200"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">
                                {(staff.full_name || '?')[0].toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-bold text-gray-900 text-sm">{staff.full_name}</p>
                              <p className="text-xs text-gray-400">{staff.email}</p>
                            </div>
                          </div>
                        </td>
                        {/* Role */}
                        <td className={STYLES.tableCell}>
                          <RoleBadge roleKey={staff.role_key} />
                        </td>
                        {/* Assigned Categories */}
                        <td className={STYLES.tableCell}>
                          <div className="flex flex-wrap gap-1.5">
                            {staff.assigned_categories.length === 0 ? (
                              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                                {t.noCategory}
                              </span>
                            ) : (
                              staff.assigned_categories.map(cat => (
                                <CategoryPill
                                  key={cat}
                                  name={cat}
                                  onRemove={() => setRemoveModal({ staff, categoryName: cat })}
                                />
                              ))
                            )}
                          </div>
                        </td>
                        {/* Actions */}
                        <td className={STYLES.tableCell + ' text-center'}>
                          <button
                            onClick={() => setAssignModal({ staff })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg text-white transition-all hover:opacity-90"
                            style={{ backgroundColor: COLORS.primary }}
                          >
                            <Plus size={12} />
                            {t.assign}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ─── Modals ─── */}
      {assignModal && (
        <AssignModal
          staff={assignModal.staff}
          categories={scopeData?.categories || []}
          assignedCategories={assignModal.staff.assigned_categories || []}
          onAssign={handleAssign}
          onClose={() => setAssignModal(null)}
          t={t}
        />
      )}

      {removeModal && (
        <ConfirmRemoveModal
          staff={removeModal.staff}
          categoryName={removeModal.categoryName}
          onConfirm={handleRemove}
          onClose={() => setRemoveModal(null)}
          t={t}
        />
      )}
    </div>
  );
};

export default GovernanceStaffScopesPage;
